const browserExtensionUrls = [
    /^chrome-extension:\/\//,
    /^moz-extension:\/\//,
    /^safari-(web-)?extension:\/\//,
];

// A query parameter or JSON key named as a credential, and a key whose whole value is secret.
const credentialName = String.raw`[\w-]*(?:token|secret|password|passwd|api[_-]?key|apikey)`;
const secretKey = /token|secret|password|passwd|api[_-]?key|apikey|authorization|cookie/i;

// Secrets in text, each with what stays of it. In a URL or a bare query string, and in a URL nested in
// another one (a redirect target), where it is percent-encoded: a signed link's signature (_hash, and the
// _expiration it covers), the credentials, and the token in the path of the links that log in, set a
// password or confirm an email. In JSON: the value of a credential key. After an authorization scheme.
const secrets = [
    [new RegExp(String.raw`((?:^|[?&;])(?:_hash|_expiration|code|key|${credentialName})=)[^&#\s"'<>\\]*`, 'gi'), '$1[Filtered]'],
    [new RegExp(String.raw`((?:%3F|%26)(?:_hash|_expiration|code|key|${credentialName})%3D)(?:(?!%26)[^&#\s"'<>\\])*`, 'gi'), '$1[Filtered]'],
    [/((?:\/|%2F)(?:reset-password|create-password|verify-email|confirm-email-change|email-confirm-login|put-online)(?:\/|%2F))[A-Za-z0-9_-]{16,}/gi, '$1[Filtered]'],
    [new RegExp(String.raw`("${credentialName}"\s*:\s*")(?:[^"\\]|\\.)*"`, 'gi'), '$1[Filtered]"'],
    [/\b(Bearer|Zoho-oauthtoken)\s+[\w.~+/=-]{8,}/gi, '$1 [Filtered]'],
];

function isPlainObject(value) {
    const prototype = Object.getPrototypeOf(value);

    return prototype === Object.prototype || prototype === null;
}

// Every text blanked, the shape kept: numbers and flags are no secret.
function filtered(value) {
    if (Array.isArray(value)) {
        return value.map(filtered);
    }

    if (value !== null && typeof value === 'object' && isPlainObject(value)) {
        return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, filtered(item)]));
    }

    return typeof value === 'string' ? '[Filtered]' : value;
}

/**
 * A copy of an event, span or breadcrumb that carries no secret (DDT-4466): no signed link's
 * signature (whoever holds a signed link opens the litigant's booking, payment or messages) and
 * no credential. The page address travels in the request, the breadcrumbs and the spans' data.
 * Only arrays and plain objects are copied: the SDK's own objects (scopes) stay as they are.
 */
export function withoutSecrets(value) {
    if (typeof value === 'string') {
        return secrets.reduce((text, [pattern, replacement]) => text.replace(pattern, replacement), value);
    }

    if (Array.isArray(value)) {
        return value.map(withoutSecrets);
    }

    if (value !== null && typeof value === 'object' && isPlainObject(value)) {
        return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, secretKey.test(key) ? filtered(item) : withoutSecrets(item)]));
    }

    return value;
}

// A request to another site can carry this page's address in its own encoding (Zoho PageSense sends the
// page's parameters as JSON), which no pattern above recognizes: it keeps its address, not its query.
// Requests to this site keep their query, scrubbed like any text.
const absoluteUrl = /\b(https?:\/\/([^/?#\s"'<>]+)[^?#\s"'<>]*)\?[^#\s"'<>]*/gi;

function withoutThirdPartyQuery(text) {
    return typeof text === 'string'
        ? text.replace(absoluteUrl, (url, address, host) => (host === globalThis.location?.host ? url : `${address}?[Filtered]`))
        : text;
}

function withoutThirdPartyQueries(span) {
    if (!/^(?:resource\.|http\.client)/.test(span?.op ?? '')) {
        return span;
    }

    const data = { ...span.data };
    for (const key of ['url', 'http.url', 'url.full'].filter((name) => name in data)) {
        data[key] = withoutThirdPartyQuery(data[key]);
    }
    if (typeof data['http.query'] === 'string' && data['server.address'] && data['server.address'] !== globalThis.location?.host) {
        data['http.query'] = '[Filtered]';
    }

    return { ...span, description: withoutThirdPartyQuery(span.description), data };
}

function withoutThirdPartyBreadcrumbQuery(breadcrumb) {
    return ['fetch', 'xhr'].includes(breadcrumb?.category) && breadcrumb.data
        ? { ...breadcrumb, data: { ...breadcrumb.data, url: withoutThirdPartyQuery(breadcrumb.data.url) } }
        : breadcrumb;
}

function eventWithoutSecrets(event) {
    const scrubbed = withoutSecrets(event);

    return Array.isArray(scrubbed?.breadcrumbs) ? { ...scrubbed, breadcrumbs: scrubbed.breadcrumbs.map(withoutThirdPartyBreadcrumbQuery) } : scrubbed;
}

function transactionWithoutSecrets(event) {
    const scrubbed = withoutSecrets(event);

    return Array.isArray(scrubbed?.spans) ? { ...scrubbed, spans: scrubbed.spans.map(withoutThirdPartyQueries) } : scrubbed;
}

/**
 * Options for Sentry.init() from the settings the server renders into
 * <meta name="sentry-browser-config"> (App\Twig\SentryExtension).
 *
 * No personal data leaves the browser, and GlitchTip ignores sessions and
 * client reports, so sending them would only cost a request per page view.
 */
export function sentryOptions(config, tracingIntegration) {
    return {
        dsn: config.dsn,
        environment: config.environment ?? undefined,
        release: config.release ?? undefined,
        tracesSampleRate: config.tracesSampleRate,
        initialScope: { tags: config.tags },
        sendDefaultPii: false,
        sendClientReports: false,
        denyUrls: browserExtensionUrls,
        // Errors carry their breadcrumbs; standalone spans (interactions) skip beforeSendTransaction.
        beforeSend: eventWithoutSecrets,
        beforeSendTransaction: transactionWithoutSecrets,
        beforeSendSpan: (span) => withoutThirdPartyQueries(withoutSecrets(span)),
        integrations: (defaults) => defaults
            .filter((integration) => integration.name !== 'BrowserSession')
            .concat(tracingIntegration),
    };
}
